import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useCustomer } from '../auth/CustomerContext';
import { useConsent } from './consent';
import { identifyLoggedIn, initTracker, track } from './tracker';

/**
 * React lepidlo trackeru: page_viewed na každou změnu cesty (i initial load)
 * a identify při přihlášeném zákazníkovi. Samotný tracker je modulový
 * singleton – tahle komponenta ho jen krmí z routeru a customer kontextu.
 * Nic nevykresluje, jen obaluje děti.
 */
export function TrackingProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { customer } = useCustomer();
  const { consent } = useConsent();

  // Flush listenery (pagehide/visibilitychange) se registrují jednou.
  useEffect(() => {
    initTracker();
  }, []);

  // page_viewed: mount i každá změna path+search. Title se čte až po
  // vykreslení – usePageMeta ho nastavuje v efektech stránek, které běží
  // dřív (děti před rodičem), takže document.title už je aktuální.
  useEffect(() => {
    track('page_viewed', {
      path: location.pathname + location.search,
      title: document.title,
    });
  }, [location.pathname, location.search]);

  // identify: při mountu s přihlášeným uživatelem i při přechodu null→user
  // (přihlášení, registrace). Endpoint je na serveru idempotentní, opakované
  // volání je no-op. Závislost na souhlasu: když zákazník souhlas udělí až
  // po přihlášení, identify se doběhne dodatečně.
  const identifiedFor = useRef<string | null>(null);
  useEffect(() => {
    if (customer === null || customer === undefined) {
      identifiedFor.current = null;
      return;
    }
    if (!consent.analytics || identifiedFor.current === customer.id) return;
    identifiedFor.current = customer.id;
    identifyLoggedIn();
  }, [customer, consent.analytics]);

  return <>{children}</>;
}
